import { LabelProps, YAxisProps } from 'recharts'
import { LineMeasureType } from '../../_types'
import { TFunction } from 'i18next'

/**
 * Data structure for additional chart lines in the line graph.
 * Based on BaseChartMeasureLine but with simplified property names for internal use.
 * Required properties match BaseChartMeasureLine requirements for line representation.
 */
export interface AdditionalChartLineData {
  /** Measure slug (corresponds to BaseChartMeasureLine.slug) */
  slug: string
  /** Unit of measurement (required, corresponds to BaseChartMeasureLine.unitOfMeasure) */
  unitOfMeasurement: string
  /** Measure color (required, corresponds to BaseChartMeasureLine.color) */
  color?: string
  /** Override measure label (optional, corresponds to BaseChartMeasureLine.label) */
  label?: string
  /** Type of the measure (required, corresponds to BaseChartMeasureLine.type) */
  type?: LineMeasureType
}

/**
 * Configuration for a Y-axis in the chart
 */
export interface UnitAxisConfig {
  unitOfMeasurement: string
  id: string
  orientation: 'left' | 'right'
  mirror: boolean
  type?: LineMeasureType
}

/**
 * Constants for axis configuration
 */
export const AXIS_ID_PREFIX = 'axis_'
const AXIS_LABEL_OFFSET = 8
const AXIS_MIRRORED_LABEL_OFFSET = 28
const AXIS_LABEL_DY = -12
const DEFAULT_MEASURE_TYPE: LineMeasureType = 'float'
export const AXIS_MIRRORED_OPACITY = 0.4
export const AXIS_TICK_MARGIN = 10

/**
 * Gets translated yes/no values for boolean measures.
 */
export const getYesNoTranslation = (t: TFunction): string => {
  return `${t('plantAnalysis.legend.common.yes')}/${t('plantAnalysis.legend.common.no')}`
}

/**
 * Formats a measure value for display in the tooltip.
 *
 * - For boolean types: Returns translated yes/no value with unit (e.g., "active YES")
 * - For float types: Returns the numeric value formatted to 2 decimal places (e.g., "12.34")
 * - For null/undefined values: Returns "--"
 *
 * @param value - The raw measure value from the data
 * @param type - The measure type: 'float' or 'boolean'
 * @param unit - Optional unit of measurement to append for boolean types
 * @param t - Translation function from i18next
 * @returns Formatted string value ready for display
 *
 * @example
 * ```ts
 * formatTooltipValue(1, 'boolean', 'active', t) // Returns "active YES"
 * formatTooltipValue(0, 'boolean', 'active', t) // Returns "active NO"
 * formatTooltipValue(12.345, 'float', 'active', t) // Returns "12.35"
 * formatTooltipValue(null, 'float', 'active', t) // Returns "--"
 * ```
 */
export const formatTooltipValue = (
  value: number | null | undefined,
  type: LineMeasureType | undefined,
  unit: string | undefined,
  t: TFunction
): string => {
  if (value == null) {
    return '--'
  }

  if (type === 'boolean') {
    return value ? t('plantAnalysis.legend.common.yes') : t('plantAnalysis.legend.common.no')
  }

  return value.toFixed(2)
}

/**
 * Formats a display value for the tooltip legend.
 *
 * - For boolean types: The value already includes yes/no and unit from formatTooltipValue, so it's returned as-is
 * - For float types: Appends the unit to the formatted numeric value
 *
 * @param formattedValue - The pre-formatted value from formatTooltipValue
 * @param type - The measure type: 'float' or 'boolean'
 * @param unit - Optional unit of measurement to append for float types
 * @returns Final formatted string for display in the tooltip legend
 *
 * @example
 * ```ts
 * formatTooltipDisplayValue('active YES', 'boolean', 'active') // Returns "active YES"
 * formatTooltipDisplayValue('12.35', 'float', 'A') // Returns "12.35 A"
 * ```
 */
export const formatTooltipDisplayValue = (
  formattedValue: string,
  type: LineMeasureType | undefined,
  unit: string | undefined
): string => {
  if (type === 'boolean') {
    // For boolean types, the value already includes yes/no and unit from formatTooltipValue
    return formattedValue
  }
  // For float types, append unit if not already included
  return `${formattedValue} ${unit ?? ''}`.trim()
}

/**
 * Builds unit axis configurations from chart lines.
 * Groups measures by their unit, creating separate Y-axes for each unique unit.
 * Note: If multiple measures share the same unit but different types, only the first type encountered is used.
 *
 * If only one unit exists, it duplicates it to ensure at least two axes
 * (this avoids spacing issues when only one unit is present).
 *
 * @param additionalChartLines - Array of chart line data configurations
 * @returns Array of axis configurations, each with a unique ID, orientation, and styling
 *
 * @example
 * ```ts
 * const lines = [
 *   { measure: 'temp', unit: '°C', type: 'float' },
 *   { measure: 'pressure', unit: 'bar', type: 'float' },
 *   { measure: 'active', unit: 'kg', type: 'boolean' }
 * ]
 * const configs = buildUnitAxisConfigs(lines)
 * // Returns 3 axis configs: one for '°C', one for 'bar', one for 'kg'
 * ```
 */
export const buildUnitAxisConfigs = (additionalChartLines: AdditionalChartLineData[]): UnitAxisConfig[] => {
  // Group measures by unit only (first type encountered is used for each unit)
  const unitTypeMap = new Map<string, { unitOfMeasurement: string; type: LineMeasureType }>()

  additionalChartLines.forEach(line => {
    // Group by unit (unit is required, so no need to check)
    if (!unitTypeMap.has(line.unitOfMeasurement)) {
      unitTypeMap.set(line.unitOfMeasurement, {
        unitOfMeasurement: line.unitOfMeasurement,
        type: line.type ?? DEFAULT_MEASURE_TYPE,
      })
    }
  })

  const uniqueUnitTypes = Array.from(unitTypeMap.values())
  // Ensure at least two axes - if only one unit, duplicate it. This avoid spacing issues when only one unit is present.
  const unitsForAxes = uniqueUnitTypes.length === 1 ? [uniqueUnitTypes[0], uniqueUnitTypes[0]] : uniqueUnitTypes

  return unitsForAxes.map((unitType, index) => ({
    unitOfMeasurement: unitType.unitOfMeasurement,
    id: `${AXIS_ID_PREFIX}${index}`,
    orientation: (index % 2 === 0 ? 'left' : 'right') as 'left' | 'right',
    mirror: index > 1,
    type: unitType.type,
  }))
}

/**
 * Builds a map from unit keys to axis IDs.
 * Maps each measure to its corresponding axis based on unit and type matching.
 * Only measures with a unit are mapped; measures without unit will use the default axis.
 *
 * @param additionalChartLines - Array of chart line data configurations
 * @param unitAxisConfigs - Array of axis configurations from buildUnitAxisConfigs
 * @returns Map where keys are units and values are axis IDs
 *
 * @example
 * ```ts
 * const lines = [{ measure: 'temp', unit: '°C', type: 'float' }]
 * const configs = buildUnitAxisConfigs(lines)
 * const map = buildAxisIdMap(lines, configs)
 * // map.get('°C') returns the axis ID for temperature measures
 * ```
 */
export const buildAxisIdMap = (
  additionalChartLines: AdditionalChartLineData[],
  unitAxisConfigs: UnitAxisConfig[]
): Map<string, string> => {
  const map = new Map<string, string>()

  additionalChartLines.forEach(line => {
    // Map measures by unit only (axes are created per unit, not per unit+type)
    // Find the first axis config that matches the unit
    const config = unitAxisConfigs.find(c => c.unitOfMeasurement === line.unitOfMeasurement)
    if (config) {
      map.set(line.unitOfMeasurement, config.id)
    }
  })

  return map
}

/**
 * Generates axis label configuration for Recharts YAxis component.
 *
 * - For boolean types: Creates label with format "(unit yes/no)"
 * - For float types: Creates label with format "(unit)"
 * - Returns undefined if no unit (no label needed)
 *
 * @param config - Axis configuration with unit, orientation, and type
 * @param t - Translation function from i18next (required for boolean types)
 * @returns LabelProps configuration for Recharts, or undefined if no label needed
 *
 * @example
 * ```ts
 * const config = { unitOfMeasurement: 'kg', type: 'boolean', orientation: 'left', mirror: false, id: 'axis_0' }
 * const label = getAxisLabel(config, t)
 * // Returns { value: "(kg SI/NO)", angle: -90, position: "insideLeft", ... }
 * ```
 */
export const getAxisLabel = (config: UnitAxisConfig, t: TFunction): LabelProps | undefined => {
  // No label if unit is missing
  if (!config.unitOfMeasurement) {
    return undefined
  }

  return {
    value: `(${config.unitOfMeasurement})`,
    angle: config.orientation === 'left' ? -90 : 90,
    position: config.orientation === 'left' ? 'insideLeft' : 'insideRight',
    offset: config.mirror ? AXIS_MIRRORED_LABEL_OFFSET : AXIS_LABEL_OFFSET,
    dy: AXIS_LABEL_DY,
    style: { opacity: config.mirror ? AXIS_MIRRORED_OPACITY : 1 },
  }
}

/**
 * Generates all YAxis component props from a unit axis configuration.
 * This function centralizes all YAxis prop logic, making it easier to maintain
 * and ensuring consistency across all axes.
 *
 * @param config - Axis configuration with unit, orientation, and type
 * @param t - Translation function from i18next (required for boolean type labels)
 * @returns Complete YAxis props object ready to be spread into the YAxis component
 *
 * @example
 * ```ts
 * const config = {
 *   id: 'axis_0',
 *   unitOfMeasurement: 'kg',
 *   orientation: 'left',
 *   mirror: false,
 *   type: 'float'
 * }
 * const props = getYAxisProps(config, t)
 * // Returns: { label: {...}, domain: [0, 'auto'], axisLine: true, ... }
 *
 * // Usage:
 * <YAxis {...props} />
 * ```
 */
export const getYAxisProps = (config: UnitAxisConfig, t: TFunction): YAxisProps => {
  return {
    label: getAxisLabel(config, t),
    domain: [0, 'auto'],
    axisLine: config.mirror ? { strokeOpacity: AXIS_MIRRORED_OPACITY } : true,
    yAxisId: config.id,
    orientation: config.orientation,
    mirror: config.mirror,
    tickMargin: AXIS_TICK_MARGIN,
    tick: config.mirror ? { opacity: AXIS_MIRRORED_OPACITY } : true,
    tickLine: config.mirror ? { strokeOpacity: AXIS_MIRRORED_OPACITY } : true,
  }
}
